// Three.js built-ins (RawShaderMaterial requires explicit declarations)
uniform mat4 projectionMatrix;
uniform mat4 viewMatrix;
uniform vec3 cameraPosition;

in vec3 position;   // unit quad (unused — the quad is built from uv)
in vec2 uv;         // 0..1, origin bottom-left

// Per-instance attributes
in vec3 aOffset;    // world position of the clip's ignition point
in vec4 aParams;    // x = size (quad edge, m), y = frame, z = ±1 mirror, w = alpha

uniform vec2  uGrid;          // sheet layout: cols, rows
uniform vec2  uAnchor;        // ignition point in tile UV (origin bottom-left)
uniform float uInset;         // half-texel inset, in tile-local units
uniform float uLogDepthBufFC; // 2.0 / log2(far + 1.0)
uniform float uNearFadeStart; // view-space distance where alpha starts fading to 0
uniform float uNearFadeEnd;   // view-space distance where alpha reaches full

out vec2  vUv;
out float vAlpha;
out float vFragDepth;

void main() {
  // Cylindrical billboard: yaw toward the camera, world up stays up. Derived
  // per-instance from the blast's own position rather than from the view
  // matrix — a 30 m billboard you fly past is big enough that a shared,
  // view-aligned normal visibly shears it as it crosses the screen.
  vec3 toCam = cameraPosition - aOffset;
  vec3 right = vec3(-toCam.z, 0.0, toCam.x);
  float rlen = length(right);
  // Camera directly overhead: every horizontal facing is equally edge-on, so
  // any fixed axis will do rather than normalising a zero vector.
  right = rlen > 1e-4 ? right / rlen : vec3(1.0, 0.0, 0.0);

  float size = aParams.x;

  // Offset the quad so the anchor lands on aOffset: the clip's ignition point
  // is near the bottom of the frame, so a centred quad would bury half the
  // blast in the ground. Mirroring negates x about that same anchor line.
  vec2 corner = (uv - uAnchor) * size;
  vec3 worldPos = aOffset
    + right * (corner.x * aParams.z)
    + vec3(0.0, corner.y, 0.0);

  gl_Position = projectionMatrix * viewMatrix * vec4(worldPos, 1.0);

  // Logarithmic depth buffer support
  gl_Position.z = log2(max(1e-6, gl_Position.w + 1.0)) * uLogDepthBufFC - 1.0;
  gl_Position.z *= gl_Position.w;
  vFragDepth = 1.0 + gl_Position.w;

  // Sheet UV. Tile rows run top-down in the image while the quad's v runs
  // bottom-up (the texture is uploaded with flipY off), hence the 1.0 - uv.y.
  // The half-texel inset keeps bilinear taps at the quad's rim from reaching
  // into the neighbouring frame.
  vec2 tileUv = vec2(uv.x, 1.0 - uv.y);
  tileUv = mix(vec2(uInset), vec2(1.0 - uInset), tileUv);
  float col = mod(aParams.y, uGrid.x);
  float row = floor(aParams.y / uGrid.x);
  vUv = (vec2(col, row) + tileUv) / uGrid;

  // Near-camera fade, as on the particles: dissolve rather than clip a hard
  // quad edge through the cockpit when the camera flies into the fireball.
  float viewDist = gl_Position.w;
  vAlpha = aParams.w * smoothstep(uNearFadeStart, uNearFadeEnd, viewDist);
}
